import { toJS } from "mobx";
import uuidv4 from "uuid/v4";
import {
  UserRepository,
  CompanyRepository,
  RuntimeRepository
} from "core/repository";
import { BasePresenter } from "core/presenter";
import UserConstraint from "core/constraint/user.constraint";
import validate from "util/validate";
import { User } from "core/entity";

class UserFormPresenter extends BasePresenter {
  initialState() {
    return {
      mode: "",
      title: "",
      loading: false,
      error: false,

      sendSuccess: false,
      errorMessage: {
        message: "",
        visible: false
      },

      isNewUser: false,
      changePassword: false,

      form: new User(),
      oldCompanyUuid: "",
      errors: {
        name: "",
        username: "",
        password: "",
        confirmPassword: "",
        permissions: "",
        company_uuid: ""
      }
    };
  }

  // FIXME: How to I18n?
  static ReservedUserUuid = {
    New: "novo",
    Mine: "minha-conta"
  };

  setUserUuid(userUuid) {
    this.userUuid = userUuid;
  }

  async onStart() {
    let title = "";
    let userUuid = this.router.stateService.params.userUuid;
    this.mode =
      userUuid === UserFormPresenter.ReservedUserUuid.New ? "add" : "edit";

    if (userUuid === UserFormPresenter.ReservedUserUuid.Mine) {
      const currentUser = await this.mRuntime.get(
        RuntimeRepository.props.currentUser
      );
      userUuid = currentUser.uuid;
    }

    switch (this.mode) {
      case "add":
        await this.addLoggedUserCompanyToFormAllowedCompaniesUuids();
        title = "Novo Usuário";
        break;
      case "edit":
        title = "Editar Usuário";
        this.setUserUuid(userUuid);
        await this.loadUser();
        break;
      default:
        break;
    }

    this.viewModel.mode = this.mode;
    this.viewModel.isNewUser = this.mode === "add";
    this.viewModel.title = title;
  }

  async addLoggedUserCompanyToFormAllowedCompaniesUuids() {
    const currentUser = await this.mRuntime.get("currentUser");
    this.viewModel.form.allowed_companies_uuids.push(currentUser.company_uuid);
  }

  async loadUser() {
    this.viewModel.loading = true;

    const result = await UserRepository.getOnline(this.userUuid);

    if (result.success) {
      let form = result.data;

      form.companySlug = await this.getUserCompany(form.company_uuid);

      delete form.password;

      this.viewModel.form = form;
      this.viewModel.oldCompanyUuid = form.company_uuid;
    } else {
      await this.sendNotification({
        type: "error",
        title: "Oooops...",
        content:
          "Não conseguimos carregar este usuário! Tente novamente em alguns minutos."
      });
    }
    this.viewModel.loading = false;
  }

  async userSalesman() {
    if (this.viewModel.form.is_salesman) {
      this.sendNotification({
        type: "confirmation",
        title: "Você tem certeza?",
        content: `Prezado, ao definir um usuário como vendedor o mesmo não terá acesso à retaguarda web do PDVend, deseja confirmar?`,
        actions: [
          {
            label: "Voltar"
          },
          {
            label: "Sim, tenho certeza!",
            type: "accent",
            callback: () => this.onSubmitForm()
          }
        ]
      });
    } else {
      this.onSubmitForm();
    }
  }

  trimParams(params) {
    params.name = params.name.trim();
    params.username = params.username.trim();
    if (params.password) {
      params.password = params.password.trim();
    }
  }

  async onSubmitForm() {
    const loggedUser = await this.mRuntime.get(
      RuntimeRepository.props.currentUser
    );
    const form = this.viewModel.form;
    this.viewModel.loading = true;
    const userParams =
      this.viewModel.mode === "add"
        ? this.newUserParams(loggedUser, form)
        : this.paramsFromForm(form);
    let response = {};

    if (this.viewModel.mode === "add") {
      await this.searchUser(userParams);
    }

    if (userParams.company_uuid === undefined) {
      userParams.company_uuid = loggedUser.company_uuid;
    }

    const isValid = this.doValidateForm(userParams);
    if (!isValid) {
      this.viewModel.loading = false;
      return;
    }
    this.trimParams(userParams);

    if (!!this.userUuid) {
      // Existing user
      response = await UserRepository.update(this.userUuid, userParams);
    } else {
      // New user
      response = await UserRepository.create(userParams);
    }

    if (isValid && response.success) {
      if (loggedUser.uuid === response.data.uuid) {
        const currentInformations = await this.mRuntime.get(
          RuntimeRepository.props.currentUser
        );
        const updateStorage = { ...currentInformations, userParams };
        this.mRuntime.set(RuntimeRepository.props.currentUser, updateStorage);
      }
      await this.sendNotification({
        type: "success",
        content: `O usuário foi ${
          this.mode === "add" ? "adicionado" : "atualizado"
        }.`
      });
      this.navigateTo("app.registration.user");
    } else {
      await this.sendNotification({
        type: "error",
        title: "Oooops...",
        content: response.error
      });
    }
    this.viewModel.loading = false;
  }

  async getUserCompany(companyUuid) {
    const repositoryResult = await CompanyRepository.getOnline(companyUuid);
    const companySlug = repositoryResult.success
      ? repositoryResult.data.slug
      : "";

    return companySlug;
  }

  doValidateForm(userParams) {
    const constraint = !!this.userUuid
      ? UserConstraint.edit
      : UserConstraint.add;
    const validation = validate(userParams, constraint, {
      fullMessages: false
    });

    if (!!validation) {
      const errors = Object.entries(validation).map(v => {
        const field = v[0];
        const errors = v[1];

        return {
          field,
          content: errors.join(", ")
        };
      });

      errors.forEach(
        error => (this.viewModel.errors[error.field] = error.content)
      );

      return false;
    }

    return true;
  }

  async searchUser(params) {
    const searchResult = await UserRepository.getListOnline({
      order: [
        {
          field: "username",
          direction: "ASC"
        }
      ],
      pagination: {
        page: 1,
        perPage: 25
      },
      filters: [
        {
          field: "username",
          value: params.username,
          operation: "EQ",
          connector: "AND"
        },
        { field: "deleted", value: "true", operation: "EQ", connector: "AND" }
      ]
    });

    if (searchResult.success && searchResult.data.length > 0) {
      const data = searchResult.data[0];
      this.setUserUuid(data.uuid);

      params.uuid = data.uuid;
      params.account_uuid = data.account_uuid;
      params.deleted = false;
    }
  }

  newUserParams(loggedUser, form) {
    const params = {
      is_salesman: form.is_salesman,
      company_uuid: form.company_uuid,
      account_uuid: loggedUser.account_uuid,
      name: form.name,
      username: form.username,
      password: form.password,
      confirmPassword: form.confirmPassword,
      uuid: uuidv4(),
      permissions: toJS(form.permissions),
      locale: "pt_BR",
      metadata: {},
      allowed_companies_uuids: form.allowed_companies_uuids
    };
    return params;
  }

  paramsFromForm(form) {
    const params = {
      is_salesman: form.is_salesman,
      company_uuid: form.company_uuid,
      account_uuid: form.account_uuid,
      name: form.name,
      username: form.username,
      uuid: form.uuid,
      permissions: toJS(form.permissions),
      locale: form.locale,
      metadata: form.metadata,
      allowed_companies_uuids: form.allowed_companies_uuids
    };

    if (
      this.viewModel.changePassword &&
      form.password &&
      form.password.length > 0 &&
      form.password === form.confirmPassword
    ) {
      return {
        ...params,
        password: form.password,
        confirmPassword: form.confirmPassword
      };
    }

    return params;
  }

  removeAllowedCompanyUuid(uuid) {
    const index = this.viewModel.form.allowed_companies_uuids.indexOf(uuid);
    this.viewModel.form.allowed_companies_uuids.splice(index, 1);
  }

  addAllowedCompanyUuid(uuid) {
    this.viewModel.form.allowed_companies_uuids.push(uuid);
  }
}

export default UserFormPresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/user/form/user-form.presenter.js